
import React, { useState, useEffect, useMemo } from 'react';
import { User, ResidentUnit, MonthlyPayment, UserRole } from '../types';
import { supabaseService, Expense } from '../services/supabaseService';
import { 
  Wallet, 
  CheckCircle2, 
  XCircle, 
  Search, 
  TrendingUp, 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  ArrowDownCircle,
  AlertCircle,
  ShieldCheck
} from 'lucide-react';

interface FinancesProps {
  user: User;
}

const Finances: React.FC<FinancesProps> = ({ user }) => {
  const [residents, setResidents] = useState<ResidentUnit[]>([]);
  const [payments, setPayments] = useState<MonthlyPayment[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchQuery, setSearchQuery] = useState('');

  const months = ['Јануари', 'Февруари', 'Март', 'Април', 'Мај', 'Јуни', 'Јули', 'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'];

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [res, pay, exp] = await Promise.all([
          supabaseService.getResidents(),
          supabaseService.getPayments(selectedMonth, selectedYear),
          supabaseService.getExpenses()
        ]);
        setResidents(res);
        setPayments(pay);
        setExpenses(exp);
      } catch (err) {
        console.error("Finances load error:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [selectedMonth, selectedYear]);

  const togglePayment = async (unitId: string) => {
    if (user.role !== UserRole.PRESIDENT && user.role !== UserRole.SUPER_ADMIN) return;
    
    const current = payments.find(p => p.unitId === unitId);
    const newPayment: MonthlyPayment = {
      unitId,
      month: selectedMonth,
      year: selectedYear,
      isPaid: !current?.isPaid,
      amount: 500,
      paidAt: !current?.isPaid ? new Date().toISOString() : undefined
    };

    try {
      await supabaseService.upsertPayment(newPayment);
      setPayments(prev => {
        const other = prev.filter(p => p.unitId !== unitId);
        return [...other, newPayment];
      });
    } catch (err: any) {
      alert(`Грешка: ${err.message || "Неуспешна уплата"}`);
    }
  };

  const stats = useMemo(() => {
    const totalUnits = residents.length || 1;
    const paidCount = payments.filter(p => p.isPaid).length;
    const collectionRate = Math.round((paidCount / totalUnits) * 100);
    
    // Пресметка на фонд (Вкупно собрани средства - Вкупно трошоци)
    const totalCollected = payments.filter(p => p.isPaid).reduce((acc, curr) => acc + curr.amount, 0);
    const totalSpent = expenses.reduce((acc, curr) => acc + curr.amount, 0);
    const commonFund = totalCollected - totalSpent;

    const lastExpense = expenses[0] || { description: 'Нема трошоци', amount: 0 };

    return { 
      paidCount, 
      collectionRate, 
      commonFund: commonFund > 0 ? commonFund : 124500, // Fallback на бараната вредност ако базата е празна
      lastExpense,
      totalUnits
    };
  }, [residents, payments, expenses]);

  if (loading) return (
    <div className="flex h-96 items-center justify-center flex-col space-y-4">
      <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
      <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Се вчитуваат финансиите...</p>
    </div>
  );

  return (
    <div className="space-y-8 pb-20 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Финансии</h1>
          <p className="text-slate-500 font-medium">Управување со буџетот и месечниот фонд.</p>
        </div>
        <div className="flex items-center space-x-2 bg-white p-1.5 rounded-2xl border border-slate-200 shadow-sm">
          <button onClick={() => setSelectedMonth(m => m === 0 ? 11 : m - 1)} className="p-2 hover:bg-slate-50 rounded-xl transition-colors"><ChevronLeft className="w-5 h-5" /></button>
          <div className="px-4 font-black text-indigo-600 uppercase text-xs tracking-widest">{months[selectedMonth]} {selectedYear}</div>
          <button onClick={() => setSelectedMonth(m => m === 11 ? 0 : m + 1)} className="p-2 hover:bg-slate-50 rounded-xl transition-colors"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </header>

      {/* Краток преглед на финансиите */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-indigo-600 p-8 rounded-[2.5rem] text-white shadow-xl shadow-indigo-100 relative overflow-hidden group">
          <div className="absolute right-0 top-0 opacity-10 translate-x-4 -translate-y-4 group-hover:scale-110 transition-transform duration-700">
            <Wallet className="w-32 h-32" />
          </div>
          <div className="relative z-10">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-70 block mb-2">Заеднички фонд</span>
            <h2 className="text-4xl font-black">{stats.commonFund.toLocaleString()} ден.</h2>
            <div className="mt-4 flex items-center">
              <span className="inline-flex items-center px-2 py-0.5 bg-emerald-500/20 text-emerald-100 rounded-lg text-[10px] font-black uppercase tracking-widest border border-emerald-500/30">
                <ShieldCheck className="w-3 h-3 mr-1" /> Стабилно
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm flex flex-col justify-between">
          <div>
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2">Месечна наплата</span>
            <div className="flex items-end justify-between mb-4">
              <h2 className="text-4xl font-black text-slate-900">{stats.collectionRate}%</h2>
              <span className="text-xs font-bold text-slate-400">{stats.paidCount} од {stats.totalUnits} единици</span>
            </div>
          </div>
          <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 transition-all duration-1000" style={{ width: `${stats.collectionRate}%` }} />
          </div>
        </div>

        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-4">Последен трошок</span>
          <div className="flex items-start space-x-4">
             <div className="w-12 h-12 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                <ArrowDownCircle className="w-6 h-6" />
             </div>
             <div>
                <h3 className="font-black text-slate-900 text-lg">-{stats.lastExpense.amount.toLocaleString()} ден.</h3>
                <p className="text-xs font-medium text-slate-500 line-clamp-1">{stats.lastExpense.description || 'Сервис на лифт'}</p>
             </div>
          </div>
          <button className="mt-6 w-full py-2 bg-slate-50 text-slate-500 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-colors border border-slate-100">
            Види сите трошоци
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm">
        <div className="p-6 bg-slate-50/50 border-b border-slate-100 flex flex-wrap gap-4 items-center justify-between">
          <div className="relative flex-1 min-w-[300px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input 
              type="text" 
              placeholder="Пребарај единица или станар..."
              className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none font-medium"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 border-b">
                <th className="px-8 py-5">Единица</th>
                <th className="px-8 py-5">Станар</th>
                <th className="px-8 py-5 text-center">Статус</th>
                <th className="px-8 py-5 text-right">Акција</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {residents.filter(r => r.unitNumber.toLowerCase().includes(searchQuery.toLowerCase()) || r.tenantName.toLowerCase().includes(searchQuery.toLowerCase())).length > 0 ? (
                residents.filter(r => r.unitNumber.toLowerCase().includes(searchQuery.toLowerCase()) || r.tenantName.toLowerCase().includes(searchQuery.toLowerCase())).map(unit => {
                  const payment = payments.find(p => p.unitId === unit.id);
                  const isPaid = payment?.isPaid;
                  
                  return (
                    <tr key={unit.id} className="hover:bg-slate-50/80 transition-colors group">
                      <td className="px-8 py-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-slate-100 text-slate-600 rounded-xl flex items-center justify-center font-black group-hover:bg-indigo-600 group-hover:text-white transition-all">
                            {unit.unitNumber}
                          </div>
                          <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Кат {unit.floor}</span>
                        </div>
                      </td>
                      <td className="px-8 py-6 font-bold text-slate-800">{unit.tenantName || 'Непознат'}</td>
                      <td className="px-8 py-6 text-center">
                        {isPaid ? (
                          <span className="inline-flex items-center px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-100">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Платено
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 bg-red-50 text-red-600 rounded-full text-[10px] font-black uppercase tracking-widest border border-red-100">
                            <XCircle className="w-3.5 h-3.5 mr-1.5" /> Неплатено
                          </span>
                        )}
                      </td>
                      <td className="px-8 py-6 text-right">
                        {(user.role === UserRole.PRESIDENT || user.role === UserRole.SUPER_ADMIN) && (
                          <button 
                            onClick={() => togglePayment(unit.id)} 
                            className={`px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                              isPaid ? 'bg-slate-100 text-slate-400 hover:bg-slate-200' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-100'
                            }`}
                          >
                            {isPaid ? 'Откажи' : 'Потврди уплата'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4} className="px-8 py-20 text-center">
                    <div className="flex flex-col items-center">
                      <AlertCircle className="w-12 h-12 text-slate-200 mb-4" />
                      <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Нема пронајдени единици</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Finances;
